// Canonical-entity resolver (PRD 00 §Pass 2 / PRD 07 §6.5).
//
// Pass 2 reconciliation produces a CorpusGlossary whose entities subsume
// clusters of per-doc terms and symbols. When a doc is rendered, the
// renderer prefers the canonical Definition over the per-doc one for
// any term/symbol whose ref appears in some entity's `member_refs`.
//
// This module is a pure function over CorpusGlossary; the renderer
// builds the resolver once per render and reads it on every row.

import type {
  CorpusEntity,
  CorpusGlossary,
  Definition,
} from "../data/types";

/** PRD 07 §Reader register: drill-down depth cap on the prereq DAG. */
export const PREREQ_MAX_DEPTH = 4;

export interface PrereqWalkEntry {
  entity: CorpusEntity;
  depth: number;
}

export interface CanonicalResolver {
  /** Lookup canonical entity by `<doc_id>:<term_id-or-symbol_id>`. */
  byRef(docId: string, refId: string): CorpusEntity | null;
  /** Convenience: returns the canonical Definition, or the fallback. */
  definitionFor(
    docId: string,
    refId: string,
    fallback: Definition | null,
  ): Definition | null;
  /**
   * Breadth-first walk of the prereq DAG from ``rootEntityId``.
   * Stops at ``maxDepth`` (default 4, per PRD 07 §Reader register).
   * Cycle-safe: each entity_id appears at most once in the result.
   * Returns an empty list for unknown root ids.
   */
  walkPrereqs(
    rootEntityId: string,
    maxDepth?: number,
  ): PrereqWalkEntry[];
}

const EMPTY: CanonicalResolver = {
  byRef: () => null,
  definitionFor: (_doc, _ref, fallback) => fallback,
  walkPrereqs: () => [],
};

export function buildCanonicalResolver(
  corpusGlossary?: CorpusGlossary | null,
): CanonicalResolver {
  if (!corpusGlossary || corpusGlossary.entities.length === 0) {
    return EMPTY;
  }
  // Build a single Map keyed by the literal ref string we'll look up.
  // Multiple entities can carry the same ref only if Pass 2 is buggy;
  // the last write wins (deterministic order — entity_id sort).
  const byRefMap = new Map<string, CorpusEntity>();
  for (const entity of corpusGlossary.entities) {
    for (const ref of entity.member_refs) {
      byRefMap.set(ref, entity);
    }
  }
  const entityById = new Map<string, CorpusEntity>(
    corpusGlossary.entities.map((e) => [e.entity_id, e]),
  );
  const dag = corpusGlossary.prereq_dag ?? {};
  return {
    byRef: (docId, refId) => byRefMap.get(`${docId}:${refId}`) ?? null,
    definitionFor: (docId, refId, fallback) => {
      const entity = byRefMap.get(`${docId}:${refId}`);
      return entity ? entity.definition : fallback;
    },
    walkPrereqs: (rootEntityId, maxDepth = PREREQ_MAX_DEPTH) =>
      walkPrereqChain(rootEntityId, entityById, dag, maxDepth),
  };
}

function walkPrereqChain(
  rootEntityId: string,
  entityById: Map<string, CorpusEntity>,
  dag: Record<string, string[]>,
  maxDepth: number,
): PrereqWalkEntry[] {
  if (!entityById.has(rootEntityId)) return [];
  const out: PrereqWalkEntry[] = [];
  const seen = new Set<string>([rootEntityId]);
  let frontier: string[] = [rootEntityId];
  let depth = 1;
  while (frontier.length > 0 && depth <= maxDepth) {
    const next: string[] = [];
    for (const parentId of frontier) {
      for (const childId of dag[parentId] ?? []) {
        if (seen.has(childId)) continue;
        const child = entityById.get(childId);
        if (!child) continue;
        seen.add(childId);
        out.push({ entity: child, depth });
        next.push(childId);
      }
    }
    frontier = next;
    depth += 1;
  }
  return out;
}
